/**
 * @license
 * Copyright 2022 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { Menu } from '@material/web/menu/internal/menu.js';
import { styles } from '@material/web/menu/internal/menu-styles.js';

export { ListItem } from '@material/web/list/internal/list-navigation-helpers.js';
export { MenuItem } from '@material/web/menu/internal/controllers/menuItemController.js';
export {
  CloseMenuEvent,
  FocusState,
  Menu,
} from '@material/web/menu/internal/controllers/shared.js';
export { Corner } from '@material/web/menu/internal/menu.js';

/**
 * @summary Menus display a list of choices on a temporary surface.
 *
 * @description
 * Menus appear when users interact with a button, action, or other control.
 *
 * They can be opened from a variety of elements, most commonly icon buttons,
 * buttons, and text fields.
 *
 * ix-menu listens for the `close-menu` and `deselect-items` events.
 *
 * - `close-menu` closes the menu when dispatched from a child element.
 * - `deselect-items` deselects all of its immediate menu-item children.
 *
 * @example
 * ```html
 * <div style="position:relative;">
 *   <button
 *       id="anchor"
 *       @click=${() => this.menuRef.value.show()}>
 *     Click to open menu
 *   </button>
 *   <!--
 *     `has-overflow` is required when using a submenu which overflows the
 *     menu's contents.
 *
 *     Additionally, `anchor` ingests an idref which do not pass through shadow
 *     roots. You can also set `.anchorElement` to an element reference if
 *     necessary.
 *   -->
 *   <ix-menu anchor="anchor" has-overflow ${ref(menuRef)}>
 *     <ix-menu-item headline="This is a headline"></ix-menu-item>
 *     <ix-sub-menu>
 *       <ix-menu-item
 *           slot="item"
 *           headline="this is a submenu item">
 *       </ix-menu-item>
 *       <ix-menu slot="menu">
 *         <ix-menu-item headline="This is an item inside a submenu">
 *         </ix-menu-item>
 *       </ix-menu>
 *     </ix-sub-menu>
 *   </ix-menu>
 * </div>
 * ```
 *
 * @final
 * @suppress {visibility}
 */

export class IxMenu extends Menu {
  static override styles = [styles];
}
